// Premium customer-facing invoice — same design family as QuoteProposal, but
// focused on AMOUNT DUE, DUE DATE, PAYMENT and WORK COMPLETED.
// Presentation only: every number comes from the server payload
// (see backend/invoice_doc.py).
import { Fragment, useMemo, useState } from "react";
import {
  FileText, Calendar, Tag, User, Briefcase, ClipboardList, ListOrdered, Check, X,
  CreditCard, Phone, Mail, Clock, Download, Linkedin, Facebook, Instagram,
  AlertTriangle, ChevronDown, Hammer, Package, Truck, Wrench,
} from "lucide-react";
import { imgSrc, money } from "./QuoteProposal";

const qty = (n) => `${Number(n || 0)}`;

const rgba = (hex, a) => {
  const h = (hex || "#FF5F15").replace("#", "");
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${a})`;
};

const STATUS = {
  draft: { label: "Draft", colour: "#52525B", icon: FileText },
  due: { label: "Due", colour: null, icon: Clock },
  partially_paid: { label: "Partially paid", colour: "#D97706", icon: Clock },
  paid: { label: "Paid", colour: "#059669", icon: Check },
  overdue: { label: "Overdue", colour: "#DC2626", icon: AlertTriangle },
  void: { label: "Void", colour: "#52525B", icon: X },
};

const CATEGORY_ICON = { labour: Hammer, material: Package, plant: Truck, service: Wrench };

const PanelHead = ({ icon: Icon, title, accent, dark = false, testid, right = null }) => (
  <div className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-3.5 ${dark ? "bg-[#18181B]" : "bg-[#F4F4F5]"}`}
    style={{ borderLeft: `3px solid ${accent}` }} data-testid={testid}>
    <div className="flex items-center gap-3 min-w-0">
      <span className="w-7 h-7 flex items-center justify-center shrink-0" style={{ background: accent }}>
        <Icon className="w-4 h-4 text-white" strokeWidth={2.4} />
      </span>
      <h2 className={`text-sm sm:text-base font-black uppercase tracking-[0.08em] ${dark ? "text-white" : "text-zinc-900"}`}>{title}</h2>
    </div>
    {right}
  </div>
);

const Blocks = ({ blocks, html, accent, testid }) => {
  if (html) {
    return <div className="prose prose-sm max-w-none text-zinc-700 [&_p]:mb-2" data-testid={testid}
      dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return (
    <div className="space-y-2" data-testid={testid}>
      {(blocks || []).map((b, i) => b.kind === "bullets" ? (
        <ul key={i} className="space-y-1.5">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-2.5 text-[13.5px] sm:text-sm leading-relaxed text-zinc-700">
              <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p key={i} className="text-[13.5px] sm:text-sm leading-relaxed text-zinc-700">{b.text}</p>
      ))}
    </div>
  );
};

const Hero = ({ doc, accent }) => {
  const org = doc.org || {};
  const cover = imgSrc(org.cover_url);
  const st = STATUS[doc.effective_status] || STATUS.due;
  const StatusIcon = st.icon;
  return (
    <header className="relative overflow-hidden bg-[#111318] px-5 sm:px-8 py-7 sm:py-8" data-testid="invoice-hero">
      {cover && <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${cover})` }} aria-hidden />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(100deg,rgba(9,9,11,0.94) 0%,rgba(9,9,11,0.80) 45%,rgba(9,9,11,0.62) 100%)" }} aria-hidden />
      <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex items-center gap-4 min-w-0">
          {org.logo_url && (
            <img src={imgSrc(org.logo_url)} alt={`${org.name} logo`} onError={event => { event.currentTarget.style.display = "none"; }}
                style={org.logo_background ? { background: org.logo_background, padding: 4, borderRadius: 4 } : undefined}
              className="h-11 sm:h-14 w-auto max-w-[150px] object-contain shrink-0" data-testid="invoice-logo" />
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-3xl font-black uppercase tracking-tight text-white leading-none break-words" data-testid="invoice-org-name">
              {org.name}
            </h1>
            {org.tagline && (
              <div className="mt-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] text-zinc-300">{org.tagline}</div>
            )}
          </div>
        </div>
        <div className="md:text-right shrink-0">
          <div className="text-base sm:text-xl font-black uppercase tracking-tight text-white">
            Invoice {doc.invoice_number}
          </div>
          <dl className="mt-2 space-y-1 text-[11px] sm:text-xs text-zinc-300">
            {doc.issue_date && <div className="flex md:justify-end gap-2"><dt className="text-zinc-400">Issued:</dt><dd className="font-semibold">{doc.issue_date}</dd></div>}
            {doc.due_date && <div className="flex md:justify-end gap-2"><dt className="text-zinc-400">Due:</dt><dd className="font-semibold">{doc.due_date}</dd></div>}
            <div className="flex md:justify-end items-center gap-2 pt-0.5">
              <dt className="text-zinc-400">Status:</dt>
              <dd>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white"
                  style={{ background: st.colour || accent }} data-testid="invoice-status">
                  <StatusIcon className="w-3 h-3" strokeWidth={3} /> {st.label}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </header>
  );
};

const ItemThumb = ({ item, accent, show }) => {
  if (!show) return null;
  if (item.image_url) {
    return <img src={imgSrc(item.image_url)} alt="" aria-hidden className="w-12 h-12 object-cover border border-zinc-200 shrink-0" />;
  }
  const Icon = CATEGORY_ICON[item.category];
  if (!Icon) return null;
  return (
    <span className="w-12 h-12 hidden sm:flex items-center justify-center border border-zinc-200 bg-zinc-50 shrink-0" aria-hidden>
      <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={2.2} />
    </span>
  );
};

const ItemsTable = ({ doc, accent }) => {
  const d = doc.display || {};
  const showQty = d.show_quantities !== false;
  const showRate = d.show_unit_prices !== false;
  const showDesc = d.show_descriptions !== false;
  const showImages = d.show_item_images !== false;
  const groups = doc.groups || [];
  const grouped = groups.length > 0;
  const bigGrouped = grouped && (doc.line_items || []).length > 25;
  const [open, setOpen] = useState(() => (bigGrouped ? {} : null));
  const blocks = grouped
    ? groups.map((g) => ({ title: g.title, key: g.key, count: g.count, subtotal: g.subtotal, items: g.items, showSub: true }))
    : (doc.sections || []).map((s, i) => ({ title: s.title, key: `s${i}`, count: (s.items || []).length, subtotal: s.subtotal, items: s.items, showSub: !!s.title && d.show_section_subtotals !== false }));
  const named = blocks.some((b) => b.title);
  const cols = showQty && showRate ? 5 : showQty ? 4 : 2;
  const isOpen = (k) => (open === null ? true : !!open[k]);

  return (
    <div data-testid="invoice-items-body">
      <table className="hidden md:table w-full border-collapse" data-testid="invoice-items-table">
        <thead>
          <tr className="bg-[#18181B] text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            <th scope="col" className="text-left px-6 py-2.5 font-bold">Description</th>
            {showQty && <th scope="col" className="text-right px-3 py-2.5 font-bold w-[70px]">Qty</th>}
            {showQty && <th scope="col" className="text-right px-3 py-2.5 font-bold w-[70px]">Unit</th>}
            {showRate && <th scope="col" className="text-right px-3 py-2.5 font-bold w-[120px]">Rate (NZD)</th>}
            <th scope="col" className="text-right px-6 py-2.5 font-bold w-[140px]">Amount (NZD)</th>
          </tr>
        </thead>
        <tbody>
          {blocks.map((b) => (
            <Fragment key={b.key}>
              {b.title && named && (
                <tr className="bg-[#18181B]">
                  <td colSpan={cols} className="px-6 py-2.5">
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-xs font-black uppercase tracking-[0.12em] text-white">{b.title}</div>
                      {open !== null && (
                        <button type="button" onClick={() => setOpen((o) => ({ ...o, [b.key]: !o[b.key] }))}
                          className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-zinc-300 hover:text-white print:hidden"
                          data-testid={`invoice-group-toggle-${b.key}`}>
                          {b.count} items · {isOpen(b.key) ? "Hide" : "View"} details
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen(b.key) ? "rotate-180" : ""}`} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {isOpen(b.key) && (b.items || []).map((li, i) => (
                <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50/70 transition-colors">
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-3.5">
                      <ItemThumb item={li} accent={accent} show={showImages} />
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-zinc-900">
                          {li.item_no && <span className="text-zinc-400 font-mono text-xs mr-1.5">{li.item_no}</span>}
                          {li.description}
                        </div>
                        {showDesc && li.detail && <div className="text-xs text-zinc-500 mt-0.5">{li.detail}</div>}
                      </div>
                    </div>
                  </td>
                  {showQty && <td className="px-3 py-3.5 text-right text-sm tabular-nums text-zinc-700">{qty(li.qty)}</td>}
                  {showQty && <td className="px-3 py-3.5 text-right text-sm text-zinc-700">{li.unit || ""}</td>}
                  {showRate && <td className="px-3 py-3.5 text-right text-sm tabular-nums text-zinc-700">{money(li.unit_price)}</td>}
                  <td className="px-6 py-3.5 text-right text-sm font-black tabular-nums text-zinc-900">{money(li.total)}</td>
                </tr>
              ))}
              {b.title && named && b.showSub && (
                <tr className="bg-[#FAFAFA] border-b border-zinc-200">
                  <td colSpan={cols - 1} className="px-6 py-2.5 text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">
                    {b.title} subtotal
                  </td>
                  <td className="px-6 py-2.5 text-right text-sm font-black tabular-nums">{money(b.subtotal)}</td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      {/* mobile cards */}
      <div className="md:hidden" data-testid="invoice-items-mobile">
        {blocks.map((b) => (
          <div key={b.key}>
            {b.title && named && (
              <div className="bg-[#18181B] px-5 py-2.5 flex items-center justify-between">
                <span className="text-[11px] font-black uppercase tracking-[0.12em] text-white">{b.title}</span>
                <span className="text-[11px] font-black tabular-nums text-zinc-300">{money(b.subtotal)}</span>
              </div>
            )}
            {(b.items || []).map((li, i) => (
              <div key={i} className="px-5 py-4 border-b border-zinc-100">
                <div className="text-[13px] font-black uppercase tracking-wide text-zinc-900">{li.description}</div>
                {showDesc && li.detail && <div className="text-xs text-zinc-500 mt-0.5">{li.detail}</div>}
                {showQty && (
                  <div className="text-xs text-zinc-500 mt-1.5 tabular-nums">
                    {qty(li.qty)} {li.unit || ""}{showRate ? ` × ${money(li.unit_price)}` : ""}
                  </div>
                )}
                <div className="mt-2.5 flex items-center justify-between border-t border-dashed border-zinc-200 pt-2.5">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400">Amount</span>
                  <span className="text-base font-black tabular-nums">{money(li.total)}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const AmountPanel = ({ doc, accent }) => {
  const t = doc.totals || {};
  const st = doc.effective_status;
  const paidSome = Number(t.paid) > 0 || Number(t.credits) > 0;
  const tint = st === "paid" ? "rgba(5,150,105,0.08)" : st === "overdue" ? "rgba(220,38,38,0.07)" : rgba(accent, 0.08);
  const label = st === "paid" ? "Paid in full" : st === "void" ? "Void — not payable" : paidSome ? "Balance due (NZD)" : "Amount due (NZD)";
  const value = st === "paid" ? t.total : t.balance;
  return (
    <div className="px-5 sm:px-8 pt-6 pb-2 flex justify-end" data-testid="invoice-amount-panel">
      <div className="w-full sm:max-w-[460px] px-5 sm:px-6 py-5" style={{ background: tint }}>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-zinc-600">Subtotal</dt><dd className="tabular-nums font-semibold">{money(t.subtotal)}</dd></div>
          {Number(t.discount) > 0 && (
            <div className="flex justify-between"><dt className="text-zinc-600">Discount</dt><dd className="tabular-nums font-semibold">-{money(t.discount)}</dd></div>
          )}
          {Number(t.retention) > 0 && (
            <div className="flex justify-between"><dt className="text-zinc-600">Retention</dt><dd className="tabular-nums font-semibold">-{money(t.retention)}</dd></div>
          )}
          {t.gst_enabled !== false && (
          <div className="flex justify-between">
            <dt className="text-zinc-600">GST {Number(t.gst_rate_pct || 15)}%{t.gst_inclusive ? " (incl.)" : ""}</dt>
            <dd className="tabular-nums font-semibold">{money(t.gst)}</dd>
          </div>
          )}
          {paidSome && (
            <>
              <div className="flex justify-between pt-1 border-t border-zinc-300/60"><dt className="text-zinc-600">Invoice total</dt><dd className="tabular-nums font-semibold">{money(t.total)}</dd></div>
              {Number(t.paid) > 0 && (
                <div className="flex justify-between"><dt className="text-zinc-600">Payments received</dt><dd className="tabular-nums font-semibold text-emerald-700" data-testid="invoice-payments-received">-{money(t.paid)}</dd></div>
              )}
              {Number(t.credits) > 0 && (
                <div className="flex justify-between"><dt className="text-zinc-600">Credits applied</dt><dd className="tabular-nums font-semibold">-{money(t.credits)}</dd></div>
              )}
            </>
          )}
        </dl>
        <div className="mt-4 pt-4 border-t border-zinc-300/70">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <span className="text-base sm:text-lg font-black tracking-tight uppercase text-zinc-900 flex items-center gap-2">
              {st === "paid" && <Check className="w-5 h-5 text-emerald-600" strokeWidth={3} />}
              {label}
            </span>
            <span className="text-3xl sm:text-4xl font-black tracking-tight tabular-nums text-zinc-900" data-testid="invoice-amount-due">
              {money(value)}
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4 flex-wrap text-xs">
            {st === "paid" ? (
              <span className="text-emerald-700 font-bold">{doc.paid_at ? `Paid ${doc.paid_at}` : "Thank you — payment received"}</span>
            ) : st === "void" ? (
              <span className="text-zinc-500 font-bold">This invoice has been voided</span>
            ) : (
              <>
                <span className="text-zinc-500">{doc.due_date_long ? `Due ${doc.due_date_long}` : ""}</span>
                {doc.days_overdue != null ? (
                  <span className="font-black text-red-600" data-testid="invoice-overdue-note">{doc.days_overdue} days overdue</span>
                ) : doc.days_remaining != null ? (
                  <span className="font-bold" style={{ color: accent }} data-testid="invoice-days-remaining">
                    {doc.days_remaining === 0 ? "Due today" : `${doc.days_remaining} days remaining`}
                  </span>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function InvoiceDocument({ doc, onDownload, preview = false }) {
  const accent = doc?.accent || "#FF5F15";
  const org = doc?.org || {};
  const client = doc?.client || {};
  const pay = doc?.payment || {};
  const help = doc?.help || {};
  const details = useMemo(() => ([
    { icon: FileText, label: "Invoice Number", value: doc.invoice_number },
    { icon: Calendar, label: "Issue Date", value: doc.issue_date },
    { icon: Calendar, label: "Due Date", value: doc.due_date ? `${doc.due_date}${doc.days_remaining != null ? ` (${doc.days_remaining} days)` : ""}` : null },
    { icon: Briefcase, label: "Job", value: doc.job_name },
    { icon: Tag, label: "Reference", value: doc.reference },
  ].filter((r) => r.value)), [doc]);
  const payRows = [["Bank Account", pay.bank_account_number], ["Account Name", pay.bank_account_name],
    ["Payment Reference", pay.reference], ["Payment Due Date", doc.due_date]].filter(([, v]) => v);
  const work = doc.work_completed || [];
  const overdue = doc.effective_status === "overdue";

  return (
    <div className="min-h-screen bg-[#F2F1EF] py-0 sm:py-8 lg:py-10 px-0 sm:px-5 print:bg-white print:py-0">
      <style>{`@media print { .print\\:hidden{display:none!important} body{background:#fff} }`}</style>
      <article className="mx-auto w-full max-w-[1180px] bg-white shadow-[0_20px_60px_-20px_rgba(9,9,11,0.28)] print:shadow-none"
        style={{ borderLeft: `6px solid ${accent}` }} data-testid="public-invoice-page">
        <Hero doc={doc} accent={accent} />

        {overdue && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-8 py-3.5 bg-red-50 border-b border-red-200" data-testid="invoice-overdue-banner">
            <div className="flex items-center gap-2.5 text-red-700">
              <AlertTriangle className="w-4 h-4" strokeWidth={2.6} />
              <span className="text-xs sm:text-sm font-black uppercase tracking-[0.1em]">
                Overdue{doc.days_overdue ? ` by ${doc.days_overdue} day${doc.days_overdue === 1 ? "" : "s"}` : ""}
              </span>
            </div>
            <span className="text-xs sm:text-sm text-red-700">
              Amount outstanding <b className="tabular-nums">{money(doc.totals?.balance)}</b>
            </span>
          </div>
        )}

        <div className="px-5 sm:px-8 pt-8 sm:pt-10 pb-7">
          <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: accent }}>Invoice</div>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-900 leading-[1.05]" data-testid="invoice-title">
            {doc.title || doc.job_name || `Invoice for ${client.name || ""}`}
          </h2>
          {doc.description && <p className="mt-3 text-sm sm:text-base text-zinc-500 max-w-3xl">{doc.description}</p>}

          <div className="mt-8 grid md:grid-cols-2 gap-8 md:gap-10">
            <div data-testid="invoice-bill-to">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Bill to</div>
              <div className="mt-3 flex items-start gap-3">
                <User className="w-5 h-5 mt-0.5 shrink-0" style={{ color: accent }} strokeWidth={2.2} />
                <div className="min-w-0">
                  <div className="text-lg sm:text-xl font-black tracking-tight text-zinc-900">{client.name}</div>
                  {client.address && <div className="mt-1 text-sm text-zinc-500 whitespace-pre-line leading-relaxed">{client.address}</div>}
                </div>
              </div>
            </div>
            {details.length > 0 && (
              <div className="md:pl-10 md:border-l md:border-zinc-200" data-testid="invoice-details">
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Invoice details</div>
                <dl className="mt-3 space-y-2.5">
                  {details.map((r) => (
                    <div key={r.label} className="flex items-center gap-3">
                      <r.icon className="w-4 h-4 text-zinc-400 shrink-0" strokeWidth={2.2} />
                      <dt className="text-sm text-zinc-500 flex-1 min-w-0">{r.label}</dt>
                      <dd className="text-sm font-bold text-zinc-900 text-right">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>

        {(doc.work_completed_html || work.length > 0) && (
          <section className="px-5 sm:px-8 pb-8" data-testid="invoice-work-completed">
            <div className="bg-[#FAFAFA] border border-zinc-100">
              <PanelHead icon={ClipboardList} title="Work completed" accent={accent} testid="work-completed-head" />
              <div className="px-4 sm:px-6 py-5">
                <Blocks blocks={work} html={doc.work_completed_html} accent={accent} testid="work-completed-body" />
              </div>
            </div>
          </section>
        )}

        <section className="px-5 sm:px-8 pb-2" data-testid="invoice-items">
          <div className="border border-zinc-100">
            <PanelHead icon={ListOrdered} title="Invoice items" accent={accent} dark testid="invoice-items-head" />
            <ItemsTable doc={doc} accent={accent} />
          </div>
        </section>

        <AmountPanel doc={doc} accent={accent} />

        {(payRows.length > 0 || pay.payment_terms || help.phone || help.email || help.hours) && (
          <section className="px-5 sm:px-8 pt-7 grid grid-cols-1 md:grid-cols-2 items-start gap-5" data-testid="invoice-payment-help">
            {(payRows.length > 0 || pay.payment_terms) && (
              <div className="min-w-0 bg-[#FAFAFA] border border-zinc-100">
                <PanelHead icon={CreditCard} title="Payment details" accent={accent} testid="payment-details-head" />
                <div className="px-4 sm:px-6 py-5">
                  {payRows.length > 0 && <dl className="space-y-3">
                    {payRows.map(([k, v]) => (
                      <div key={k} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-1 sm:gap-4">
                        <dt className="text-sm text-zinc-500">{k}</dt>
                        <dd className="min-w-0 text-sm font-semibold text-zinc-900 tabular-nums [overflow-wrap:anywhere]">{v}</dd>
                    </div>
                  ))}
                </dl>}
                {pay.payment_terms && <section className={payRows.length ? "mt-5 border-t border-zinc-200 pt-5" : ""} aria-label="Payment terms">
                  <h3 className="text-sm font-semibold text-zinc-900 mb-3">Payment Terms</h3>
                  <div className="max-w-prose min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere] text-sm font-normal leading-relaxed text-zinc-700" data-testid="invoice-payment-terms">{pay.payment_terms}</div>
                </section>}
                </div>
              </div>
            )}
            {(help.phone || help.email || help.hours) && (
              <div className="min-w-0 [overflow-wrap:anywhere] bg-[#FAFAFA] border border-zinc-100">
                <PanelHead icon={Phone} title="Need help?" accent={accent} testid="need-help-head" />
                <div className="px-4 sm:px-6 py-5">
                  <p className="text-sm text-zinc-500">If you have any questions about this invoice, please contact us.</p>
                  <div className="mt-3 space-y-2">
                    {help.phone && (
                      <a href={`tel:${help.phone.replace(/\s/g, "")}`} className="flex items-center gap-2.5 text-sm font-semibold text-zinc-800 hover:underline">
                        <Phone className="w-4 h-4" style={{ color: accent }} strokeWidth={2.2} /> {help.phone}
                      </a>
                    )}
                    {help.email && (
                      <a href={`mailto:${help.email}`} className="flex items-center gap-2.5 text-sm font-semibold text-zinc-800 hover:underline break-all">
                        <Mail className="w-4 h-4 shrink-0" style={{ color: accent }} strokeWidth={2.2} /> {help.email}
                      </a>
                    )}
                    {help.hours && (
                      <div className="flex items-center gap-2.5 text-sm text-zinc-600">
                        <Clock className="w-4 h-4" style={{ color: accent }} strokeWidth={2.2} /> {help.hours}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        <div className="px-5 sm:px-8 pt-7 pb-8 print:hidden">
          <button type="button" onClick={onDownload} disabled={preview}
            className="w-full inline-flex items-center justify-center gap-2.5 px-8 py-4 text-sm font-black uppercase tracking-[0.12em] text-white transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-45 disabled:hover:translate-y-0"
            style={{ background: accent }} data-testid="invoice-download-pdf">
            <Download className="w-4 h-4" strokeWidth={2.6} /> Download invoice PDF
          </button>
          {preview && (
            <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: accent }} data-testid="invoice-preview-note">
              Preview only — this is exactly what your customer sees
            </p>
          )}
        </div>

        <footer className="px-5 sm:px-8 py-6 border-t border-zinc-100 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="invoice-footer">
          <div className="min-w-0">
            <div className="text-sm font-black text-zinc-900">{org.name}</div>
            <div className="mt-1 text-[11px] text-zinc-500 flex flex-wrap gap-x-3 gap-y-1">
              {org.gst_number && <span>GST {org.gst_number}</span>}
              {org.nzbn && <span>NZBN {org.nzbn}</span>}
              {org.address && <span>{org.address.replace(/\n/g, ", ")}</span>}
              {org.phone && <span>{org.phone}</span>}
              {org.email && <span className="break-all">{org.email}</span>}
              {org.website && (
                <a href={org.website.startsWith("http") ? org.website : `https://${org.website}`} target="_blank" rel="noopener noreferrer" className="underline">{org.website}</a>
              )}
            </div>
            {doc.footer_note && <div className="mt-2 text-[11px] italic text-zinc-400">{doc.footer_note}</div>}
          </div>
          <div className="flex items-center gap-8">
            {(org.socials || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">Follow us</div>
                <div className="mt-1.5 flex items-center gap-2">
                  {org.socials.map((s) => {
                    const Icon = s.kind === "linkedin" ? Linkedin : s.kind === "facebook" ? Facebook : Instagram;
                    return (
                      <a key={s.kind} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.kind}
                        className="w-7 h-7 flex items-center justify-center bg-zinc-900 text-white hover:opacity-80 transition-opacity">
                        <Icon className="w-3.5 h-3.5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            {!doc.hide_fieldlink_branding && <div className="shrink-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">Powered by</div>
              <a href="https://fieldlink.co.nz" target="_blank" rel="noopener noreferrer"
                className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-black uppercase tracking-tight text-zinc-900">
                <span className="w-5 h-5 flex items-center justify-center bg-[#FF5F15] text-white text-[9px] font-black">FL</span>
                Fieldlink
              </a>
            </div>}
          </div>
        </footer>
      </article>
    </div>
  );
}
